<script setup lang="ts">
// Ports legacy OModal (189 call-sites) onto Headless UI Dialog — focus-trap, Esc, scroll-lock
// and a11y come for free. Two-way `open` via defineModel; title/default/footer slots.
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import { computed, type CSSProperties } from 'vue'

import type { ModalProps } from './types'

import { injectUiLabels } from '@/shared/i18n'
import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, useOverlayFocusReturn } from '@/shared/ui/overlay'

const open = defineModel<boolean>({ default: false })

/**
 * Attributes reach the dialog root explicitly. This component's own root is `FloatingLayer`, which
 * declares `inheritAttrs: false` and, in its `as="template"` branch, renders nothing but the slot —
 * so a `class` or `data-testid` written on this component was silently DROPPED before ever reaching
 * the DOM. Harmless while every host kept its styling in a scoped style block (scoped rules
 * reach slot content through the data-v attribute, not through this root class), it stopped being
 * harmless once host CSS moved onto the component tag as Tailwind utilities: those utilities were
 * dead, and a `data-testid` on the tag was unfindable.
 */
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<ModalProps>(), {
  size: 'md',
  closeOnOutside: true,
  hideClose: false,
  overflowVisible: false,
})

const emit = defineEmits<{ close: [] }>()
const uiLabels = injectUiLabels()
const resolvedCloseLabel = computed(() => props.closeLabel ?? uiLabels.close)
const { setFocusScope, completeLeave } = useOverlayFocusReturn(open)
const panelStyle = computed<CSSProperties | undefined>(() =>
  props.width ? { maxWidth: props.width } : undefined,
)
let blockNextOutsideClose = false

function close(): void {
  open.value = false
  emit('close')
}

function onBackdropPointerDown(): void {
  if (props.closeOnOutside) return
  blockNextOutsideClose = true
  setTimeout(() => {
    blockNextOutsideClose = false
  })
}

// Headless emits the same close event for Escape and outside interaction. Keep the explicit
// pointer marker narrow so closeOnOutside can block the backdrop without disabling Escape.
//
// A confirm/alert dialog raised from inside this modal (e.g. `useConfirm()`) is a sibling
// top-level mount, not this Dialog's Vue-tree descendant — Headless UI's own outside-press
// detection only recognizes true descendants, so a click on that dialog's own buttons also
// registers as "outside" THIS modal and would otherwise close it underneath the still-open
// confirm. Only the topmost blocking overlay may act on its own reflexive close.
function onDialogClose(isTopmostBlocking: boolean): void {
  if (props.hideClose || blockNextOutsideClose || !isTopmostBlocking) {
    blockNextOutsideClose = false
    return
  }
  close()
}
</script>

<template>
  <FloatingLayer
    v-slot="{ style: layerStyle, layerAttributes, isTopmostBlocking }"
    kind="modal"
    :active="open"
    as="template"
  >
    <TransitionRoot :show="open" as="template" @after-leave="completeLeave">
      <Dialog
        v-bind="{ ...$attrs, ...layerAttributes }"
        class="ui-modal"
        :style="[$attrs.style as CSSProperties, layerStyle]"
        @close="onDialogClose(isTopmostBlocking)"
      >
        <TransitionChild
          as="template"
          enter="ui-modal-fade-enter"
          enter-from="ui-modal-fade-out"
          enter-to="ui-modal-fade-in"
          leave="ui-modal-fade-leave"
          leave-from="ui-modal-fade-in"
          leave-to="ui-modal-fade-out"
        >
          <div class="ui-modal__backdrop" @pointerdown="onBackdropPointerDown" />
        </TransitionChild>

        <div :ref="setFocusScope" class="ui-modal__viewport">
          <div class="ui-modal__center">
            <TransitionChild
              as="template"
              enter="ui-modal-pop-enter"
              enter-from="ui-modal-pop-out"
              enter-to="ui-modal-pop-in"
              leave="ui-modal-pop-leave"
              leave-from="ui-modal-pop-in"
              leave-to="ui-modal-pop-out"
            >
              <DialogPanel
                class="ui-modal__panel"
                :class="[
                  `ui-modal__panel--${size}`,
                  { 'ui-modal__panel--overflow-visible': overflowVisible },
                ]"
                :style="panelStyle"
              >
                <button v-if="!hideClose" type="button" class="ui-modal__close" @click="close">
                  <span class="ui-sr-only">{{ resolvedCloseLabel }}</span>
                  <Icon name="x" :size="14" />
                </button>

                <DialogTitle v-if="title || $slots.title" as="h3" class="ui-modal__title">
                  <slot name="title">{{ title }}</slot>
                </DialogTitle>
                <p v-if="subtitle || $slots.subtitle" class="ui-modal__subtitle">
                  <slot name="subtitle">{{ subtitle }}</slot>
                </p>

                <div class="ui-modal__body">
                  <slot name="body"><slot /></slot>
                </div>

                <div v-if="$slots.buttons || $slots.footer" class="ui-modal__footer">
                  <slot name="buttons"><slot name="footer" /></slot>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </FloatingLayer>
</template>

<style scoped>
.ui-modal__backdrop {
  position: fixed;
  inset: 0;
  background: var(--ui-overlay-backdrop);
}
.ui-modal__viewport {
  position: fixed;
  inset: 0;
  overflow-y: auto;
}
.ui-modal__center {
  display: flex;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}
.ui-modal__panel {
  position: relative;
  width: 100%;
  max-height: var(--ui-modal-panel-max-height);
  overflow: auto;
  padding: var(--ui-modal-panel-padding);
  background: var(--ui-color-surface);
  color: var(--ui-color-fg);
  border-radius: var(--ui-modal-panel-radius);
  box-shadow: var(--ui-overlay-panel-shadow);
  font-family: var(--ui-font-sans);
}
.ui-modal__panel--overflow-visible {
  overflow: visible;
}
.ui-modal__panel--sm {
  max-width: 24rem;
}
.ui-modal__panel--md {
  max-width: 32rem;
}
.ui-modal__panel--lg {
  max-width: 42rem;
}
.ui-modal__panel--xl {
  max-width: 56rem;
}
.ui-modal__panel--2xl {
  max-width: 72rem;
}
.ui-modal__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--ui-radius-full);
  background: var(--ui-color-surface-sunken);
  color: var(--ui-color-muted);
  transition: color 0.15s ease;
}
.ui-modal__close:hover {
  color: var(--ui-color-fg);
}
.ui-modal__title {
  margin: 0 0 1.25rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
}
.ui-modal__title:has(+ .ui-modal__subtitle) {
  margin-bottom: 0.25rem;
}
.ui-modal__subtitle {
  margin: 0 0 1.25rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
}
.ui-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}
.ui-modal__body {
  margin-top: var(--ui-space-xs);
}

@media (min-width: 40rem) {
  .ui-modal__center {
    padding: 0;
  }
  .ui-modal__panel {
    margin-block: 2rem;
  }
}

/* Transitions (global classes so Headless can toggle them regardless of scoping) */
.ui-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
</style>

<style>
.ui-modal {
  position: relative;
}
.ui-modal-fade-enter {
  transition: opacity var(--ui-overlay-enter-duration) ease-out;
}
.ui-modal-fade-leave {
  transition: opacity var(--ui-overlay-leave-duration) ease-in;
}
.ui-modal-fade-out {
  opacity: 0;
}
.ui-modal-fade-in {
  opacity: 1;
}
.ui-modal-pop-enter {
  transition:
    opacity var(--ui-overlay-enter-duration) ease-out,
    transform var(--ui-overlay-enter-duration) ease-out;
}
.ui-modal-pop-leave {
  transition:
    opacity var(--ui-overlay-leave-duration) ease-in,
    transform var(--ui-overlay-leave-duration) ease-in;
}
.ui-modal-pop-out {
  opacity: 0;
  transform: translateY(1rem) scale(0.97);
}
.ui-modal-pop-in {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
